"use client";
import { useEffect, useRef, useState } from "react";
import { Menu, ShoppingCart, X } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useCart } from "../context/cartContext";
import { WhatsAppLink } from "./StoreLinks";
import { STORE } from "../../../lib/site";

interface HeaderProps {
  searchQuery?: string;
  setSearchQuery?: (query: string) => void;
}
const links = [
  { href: "/#offerings", label: "Products We Supply", section: "offerings" },
  { href: "/#our-store", label: "Our Store", section: "our-store" },
  { href: "/#location", label: "Location", section: "location" },
  { href: "/about", label: "About", section: "" },
];
export function StoreLogo() {
  return (
    <Link href="/" className="store-logo" aria-label="Shop House — home">
      <Image
        src="/store-logo.webp"
        alt="Shop House General Trading"
        width={440}
        height={150}
        sizes="204px"
        priority
      />
    </Link>
  );
}
export default function Header(_props: HeaderProps) {
  void _props;
  const pathname = usePathname();
  const { cartCount } = useCart();
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState("");
  const toggle = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLElement>(null);
  const shopping =
    pathname.startsWith("/products") ||
    pathname.startsWith("/cart") ||
    pathname === "/order-confirmation" ||
    pathname === "/orders";
  useEffect(() => {
    setOpen(false);
  }, [pathname]);
  useEffect(() => {
    if (!open) return;
    panel.current?.querySelector<HTMLAnchorElement>("a")?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        toggle.current?.focus();
      }
    };
    document.addEventListener("keydown", closeOnEscape);
    const media = window.matchMedia("(min-width: 1024px)");
    const resize = () => {
      if (media.matches) setOpen(false);
    };
    media.addEventListener("change", resize);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      media.removeEventListener("change", resize);
    };
  }, [open]);
  useEffect(() => {
    if (pathname !== "/") {
      setSection("");
      return;
    }
    const update = () => {
      const active = links
        .filter((item) => item.section)
        .map((item) => document.getElementById(item.section))
        .filter((node): node is HTMLElement => !!node)
        .reverse()
        .find((node) => node.getBoundingClientRect().top <= 160);
      setSection(active?.id || "");
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("hashchange", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("hashchange", update);
    };
  }, [pathname]);
  const navLinks = links.map((item) => (
    <Link
      key={item.href}
      href={item.href}
      aria-current={
        (
          item.section
            ? pathname === "/" && section === item.section
            : pathname === item.href
        )
          ? "location"
          : undefined
      }
      onClick={() => {
        setOpen(false);
        if (open) toggle.current?.focus();
      }}
    >
      {item.label}
    </Link>
  ));
  return (
    <header
      className="store-header"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <div className="store-container header-inner">
        <StoreLogo />
        <nav className="desktop-nav" aria-label="Main navigation">
          {navLinks}
        </nav>
        <div className="header-actions">
          {shopping && (
            <Link
              className="cart-link"
              href="/cart"
              aria-label={`Cart, ${cartCount} items`}
            >
              <ShoppingCart size={21} />
              <span aria-live="polite">{cartCount}</span>
            </Link>
          )}
          <WhatsAppLink label="WhatsApp" source="header" />
          <button
            ref={toggle}
            className="menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-navigation"
            onClick={() => setOpen(!open)}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {open && (
        <nav
          ref={panel}
          id="mobile-navigation"
          className="mobile-nav"
          aria-label="Mobile navigation"
        >
          {navLinks}
          <Link href="/products" onClick={() => setOpen(false)}>
            Browse products →
          </Link>
          <a href={`tel:${STORE.mobile}`}>Call {STORE.mobileLocalDisplay}</a>
        </nav>
      )}
    </header>
  );
}
